import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import { Fetcher } from "../components/Fetcher";
import { getSubscriptionList } from "../services/api/subscription.api";
import { SubscriptionSummary } from "../models/Subscription";
import { Pagination } from "../components/Pagination";
import { DEFAULT_PAGING_SIZE } from "../constants/index";
import { useForceUpdate } from "../utils/hooks";
import { MessageCard } from "../components/MessageCard";
import { AddNewSubscription } from "../components/AddNewSubscription";
import { AddNewSubscriptionRequestInitialValue } from "../models/Requests/AddNewSubscriptionRequest";
import { PowFeature } from "../components/PowFeature";
import { POW_FEATURES } from "../services/powFeatures.service";
import { SubscriptionDisplayCard } from "../components/SubscriptionDisplayCard";
import { ListItem } from "@material-ui/core";
import { SubscriptionFilter } from "../components/SubscriptionFilter";

interface SubscriptionListPageProps {
	// product id used to fetch the subscription
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	titleWrapper: {
		maxWidth: theme.breakpoints.values.md,
		display: "flex",
		justifyContent: "space-between",
	},
	title: {
		marginBottom: theme.spacing(3),
	},
	list: {
		maxWidth: theme.breakpoints.values.md,
	},
	listItem: {
		boxShadow: theme.shadows[1],
		marginBottom: theme.spacing(2),
	},
	listSecondary: {
		marginTop: theme.spacing(0.5),
		fontSize: theme.typography.caption.fontSize,
	},
	details: {
		fontSize: theme.typography.body2.fontSize,
	},
	detailsItem: {
		marginTop: theme.spacing(1),
	},
}));

const renderPage = (
	data: SubscriptionSummary[],
	productId: string,
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
) => {
	return (
		<div data-testid="subscription-list">
			{data && data.length > 0 ? (
				<div>
					<List className={classes.list}>
						{data.map((subscription) => (
							<ListItem
								className={classes.listItem}
								key={subscription.subscriptionId}
								data-testid="subscription-list-item"
							>
								<SubscriptionDisplayCard productId={productId} subscription={subscription}></SubscriptionDisplayCard>
							</ListItem>
						))}
					</List>
					<Pagination
						pageNumber={pageNumber}
						rowsPerPage={rowsPerPage}
						setPageFunction={setPageNumber}
						setRowsPerPageFunction={setRowsPerPage}
						currentPageLength={data.length}
					></Pagination>
				</div>
			) : (
				<MessageCard message="No subscriptions found." textAlign="left" />
			)}
		</div>
	);
};

/**
 * Fetch and display product subscriptions in a list
 * @param props
 */
export const SubscriptionListPage = (props: SubscriptionListPageProps) => {
	const classes = useStyles();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const forceUpdate = useForceUpdate();
	const [filters, onFilter] = React.useState({ companyName: "", showExpired: false });

	return (
		<div>
			<div className={classes.titleWrapper}>
				<Typography className={classes.title} variant="h4" data-testid="subscription-list-page">
					Subscriptions
				</Typography>
				<div>
					<PowFeature feature={POW_FEATURES.paSubscriptions_Add}>
						<AddNewSubscription
							productId={props.productId}
							done={forceUpdate}
							initialValues={AddNewSubscriptionRequestInitialValue}
						/>
					</PowFeature>
				</div>
			</div>
			<SubscriptionFilter filters={filters} onFilter={onFilter} />
			<Fetcher action={getSubscriptionList(props.productId, { pageNumber, pageSize: rowsPerPage }, filters)}>
				{(response) =>
					renderPage(
						response && response.data,
						props.productId,
						classes,
						setPageNumber,
						setRowsPerPage,
						pageNumber,
						rowsPerPage,
					)
				}
			</Fetcher>
		</div>
	);
};
